<script lang="ts">
  import {
    flashFirmware,
    flashFirmwareUpload,
    fetchFlashLog,
    ApiError,
  } from "../lib/api";
  import { push } from "svelte-spa-router";
  import { onDestroy } from "svelte";
  import type { FlashStatus } from "@diy-avatar/shared/admin";

  export let label: string;

  const POLL_MS = 700;

  let board: "esp8266" | "esp32" = "esp8266";
  let file: File | null = null;

  let running = false;
  let flashId: string | null = null;
  let since = 0;
  let lines: string[] = [];
  let status: FlashStatus = "running";
  let rc: number | null = null;
  let error: string | null = null;

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let logEl: HTMLPreElement | null = null;

  function reset() {
    lines = [];
    since = 0;
    status = "running";
    rc = null;
    error = null;
  }

  function stopPolling() {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }

  function scheduleNext() {
    pollTimer = setTimeout(poll, POLL_MS);
  }

  async function poll() {
    if (!flashId) return;
    try {
      const res = await fetchFlashLog(label, flashId, since);
      if (res.lines.length > 0) {
        lines = [...lines, ...res.lines];
        // Auto-scroll to bottom after the DOM updates.
        queueMicrotask(() => {
          if (logEl) logEl.scrollTop = logEl.scrollHeight;
        });
      }
      since = res.next;
      status = res.status;
      rc = res.rc;
      if (status === "ok" || status === "error") {
        running = false;
        stopPolling();
        return;
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      // Transient poll error — keep trying; surface it but don't stop.
      error = err instanceof Error ? err.message : "poll failed";
    }
    scheduleNext();
  }

  function startPolling(id: string) {
    flashId = id;
    running = true;
    reset();
    stopPolling();
    scheduleNext();
  }

  async function doFlashRepo() {
    if (running) return;
    if (
      !confirm(
        `Flash prebuilt ${board} firmware to "${label}"? This overwrites the device ESP firmware.`,
      )
    ) {
      return;
    }
    error = null;
    running = true;
    try {
      const res = await flashFirmware(label, { source: "repo", board });
      startPolling(res.flash_id);
    } catch (err) {
      running = false;
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "flash failed";
    }
  }

  function readFileBase64(f: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("file read failed"));
      reader.onload = () => {
        const result = reader.result as string;
        // result is a data URL: strip the "data:...;base64," prefix.
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.readAsDataURL(f);
    });
  }

  async function doFlashUpload() {
    if (running) return;
    if (!file) {
      error = "Pick a .bin file first.";
      return;
    }
    if (
      !confirm(
        `Upload "${file.name}" and flash it to "${label}" (${board})? This overwrites the device ESP firmware.`,
      )
    ) {
      return;
    }
    error = null;
    running = true;
    try {
      const b64 = await readFileBase64(file);
      const res = await flashFirmwareUpload(label, b64, board, file.name);
      startPolling(res.flash_id);
    } catch (err) {
      running = false;
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "upload failed";
    }
  }

  function onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    file = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  onDestroy(stopPolling);
</script>

<section>
  <h1>Flash firmware</h1>
  <p class="hint">
    Flashes the device ESP via <code>esptool</code>. The flash is FORCED — it
    always runs (no checksum gate). Output streams below.
  </p>

  <label class="board">
    Board
    <select bind:value={board} disabled={running} data-testid="flash-board">
      <option value="esp8266">esp8266</option>
      <option value="esp32">esp32</option>
    </select>
  </label>

  <div class="row">
    <button
      type="button"
      on:click={doFlashRepo}
      disabled={running}
      data-testid="flash-repo"
    >
      Flash firmware (from repo)
    </button>
  </div>

  <fieldset class="upload">
    <legend>Custom firmware</legend>
    <p class="hint">
      ESP32 custom images must be full/merged (flashed at <code>0x0</code>).
    </p>
    <input
      type="file"
      accept=".bin"
      on:change={onFileChange}
      disabled={running}
      data-testid="flash-file"
    />
    <button
      type="button"
      on:click={doFlashUpload}
      disabled={running || !file}
      data-testid="flash-upload"
    >
      Upload &amp; flash
    </button>
  </fieldset>

  {#if error}
    <p class="error" data-testid="flash-error">{error}</p>
  {/if}

  {#if flashId}
    <div class="result">
      <div class="status-line">
        {#if status === "running"}
          <span class="running" data-testid="flash-status">Flashing…</span>
        {:else if status === "ok"}
          <span class="ok" data-testid="flash-status">Success (rc={rc})</span>
        {:else}
          <span class="fail" data-testid="flash-status">Failed (rc={rc})</span>
        {/if}
      </div>
      <pre bind:this={logEl} class="log" data-testid="flash-log">{lines.join("\n")}</pre>
    </div>
  {/if}
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  .hint {
    color: #9ca3af;
    font-size: 0.85rem;
  }
  .board {
    display: grid;
    gap: 0.2rem;
    color: #d1d5db;
    max-width: 200px;
    margin-bottom: 0.75rem;
  }
  select {
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
    padding: 0.4rem;
  }
  .row {
    margin-bottom: 0.75rem;
  }
  button {
    padding: 0.55rem 0.8rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 6px;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .upload {
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    display: grid;
    gap: 0.5rem;
    justify-items: start;
    margin-bottom: 0.75rem;
  }
  .upload legend {
    color: #9ca3af;
    font-size: 0.8rem;
    padding: 0 0.3rem;
  }
  .result {
    margin-top: 0.75rem;
  }
  .status-line {
    margin-bottom: 0.4rem;
    font-weight: 600;
  }
  .running {
    color: #fbbf24;
  }
  .ok {
    color: #6ee7b7;
  }
  .fail {
    color: #fca5a5;
  }
  .log {
    background: #0f172a;
    color: #e5e7eb;
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.6rem;
    max-height: 320px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.8rem;
    margin: 0;
  }
  .error {
    color: #fca5a5;
  }
</style>
